import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"
import { cx } from "@/lib/utils/cx"
import { OUTLINES, type BodyType } from "../outlines"
import type { Corner } from "../types"

// Once per visit, before the camera first takes a photo by itself: what
// that looks like, played once on a little viewfinder (the brackets close
// in on a car, turn green, the ring fills, the shutter goes). Then it gets
// out of the way by itself; "Got it", a tap anywhere or Escape (the camera
// screen) skips it. Reduced motion: the end of it, still.

export const INTRO_COPY = {
  title: "It takes the photo for you.",
  body: "Fit the whole car in the frame. When it turns green, hold still.",
  done: "Got it",
}

// framing → green → taken, then it goes.
const STEPS_MS = { lock: 1100, snap: 2200, done: 3500 } as const

// The little viewfinder (SVG units): the car drawn in the middle, the
// brackets hugging it once framed.
const VF = { w: 220, h: 140 }
const CAR = { x: 38, y: 34, w: 144, h: 72 }
const HUG = { x: 28, y: 24, w: 164, h: 92 }
const ARM = 16

function brackets({ x, y, w, h }: typeof HUG): string {
  const r = x + w
  const b = y + h
  return [
    `M${x} ${y + ARM}V${y}H${x + ARM}`,
    `M${r - ARM} ${y}H${r}V${y + ARM}`,
    `M${r} ${b - ARM}V${b}H${r - ARM}`,
    `M${x + ARM} ${b}H${x}V${b - ARM}`,
  ].join("")
}

export function AutoPhotoIntro({ corner, body, onDone }: { corner: Corner; body: BodyType; onDone: () => void }) {
  const reduce = useReducedMotion()
  const [step, setStep] = useState<"frame" | "lock" | "snap">(reduce ? "snap" : "frame")
  const done = useRef(onDone)
  useLayoutEffect(() => {
    done.current = onDone
  })
  // Once, however it's dismissed.
  const finished = useRef(false)
  const finish = () => {
    if (finished.current) return
    finished.current = true
    done.current()
  }
  const button = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    button.current?.focus({ preventScroll: true })
    const timers = [
      ...(reduce
        ? []
        : [setTimeout(() => setStep("lock"), STEPS_MS.lock), setTimeout(() => setStep("snap"), STEPS_MS.snap)]),
      setTimeout(() => {
        if (finished.current) return
        finished.current = true
        done.current()
      }, STEPS_MS.done),
    ]
    return () => timers.forEach(clearTimeout)
  }, [reduce])

  const o = OUTLINES[body][corner]
  const green = step !== "frame"
  const ease = [0.2, 0, 0, 1] as const
  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auto-photo-intro-title"
      aria-describedby="auto-photo-intro-body"
      data-testid="auto-photo-intro"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={finish}
      className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/80 px-8 text-center backdrop-blur-[2px]"
    >
      <div className="relative">
        <svg viewBox={`0 0 ${VF.w} ${VF.h}`} className="w-[220px] overflow-visible" aria-hidden>
          <rect x={0.5} y={0.5} width={VF.w - 1} height={VF.h - 1} rx={18} fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.12)" />
          <svg x={CAR.x} y={CAR.y} width={CAR.w} height={CAR.h} viewBox={`0 0 ${o.viewBox.w} ${o.viewBox.h}`} overflow="visible">
            <path d={o.body} fill="none" stroke="white" strokeWidth={1.8} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
          <motion.path
            d={brackets(HUG)}
            fill="none"
            strokeWidth={3.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cx("transition-[stroke] duration-200", green ? "stroke-signal-on-dark" : "stroke-white")}
            style={{ transformBox: "view-box", transformOrigin: `${VF.w / 2}px ${VF.h / 2}px` }}
            initial={reduce ? false : { scale: 1.22, opacity: 0.6 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.9, ease }}
          />
        </svg>
        {/* The shutter going off. */}
        {step === "snap" && !reduce && (
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[18px] bg-white"
            initial={{ opacity: 0.85 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.35, ease }}
          />
        )}
      </div>

      {/* The shutter: its ring fills while it holds, then a check. */}
      <div className="relative mt-5 grid size-14 place-items-center" aria-hidden>
        <svg viewBox="0 0 56 56" className="absolute inset-0 size-14 -rotate-90">
          <circle cx="28" cy="28" r="25" fill="none" strokeWidth="4" stroke="rgba(255,255,255,0.35)" />
          <motion.circle
            cx="28"
            cy="28"
            r="25"
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
            className="stroke-signal-on-dark"
            pathLength={1}
            strokeDasharray="1 1"
            initial={false}
            animate={{ strokeDashoffset: green ? 0 : 1 }}
            transition={{ duration: reduce ? 0 : 1, ease: "linear" }}
          />
        </svg>
        <span className={cx("grid size-10 place-items-center rounded-full bg-white transition-transform duration-150", step === "snap" && !reduce && "scale-90")}>
          {step === "snap" && (
            <svg viewBox="0 0 24 24" className="size-5 text-pine">
              <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      </div>

      <h2 id="auto-photo-intro-title" className="mt-6 text-[22px] font-semibold leading-snug text-white">
        {INTRO_COPY.title}
      </h2>
      <p id="auto-photo-intro-body" className="mt-2 max-w-[300px] text-[16px] leading-snug text-white/75">
        {INTRO_COPY.body}
      </p>
      <button
        ref={button}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          finish()
        }}
        className="mt-6 h-12 rounded-full bg-white px-7 text-[16px] font-semibold text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        {INTRO_COPY.done}
      </button>
    </motion.div>
  )
}
